<script setup lang="ts">
/**
 * Shared layout shell for every blog post. Each post is a Vue page that
 * imports this component and passes its slug as a prop; everything else
 * (header, breadcrumbs, related posts, CTA, footer) is rendered here so
 * individual post files stay focused on content.
 *
 * Usage:
 *   <BlogPostShell slug="manage-10-weddings-simultaneously-without-losing-your-mind">
 *     <h2>...</h2>
 *     ...
 *   </BlogPostShell>
 */
import { findPost, relatedPosts, VERTICAL_LABELS } from '~/utils/blogPosts'

const props = defineProps<{ slug: string }>()

const post = computed(() => findPost(props.slug))
const related = computed(() => post.value ? relatedPosts(props.slug, post.value.vertical, 3) : [])

if (post.value) {
  useSeoMeta({
    title: post.value.title,
    description: post.value.seo_description ?? post.value.excerpt,
    ogTitle: post.value.title,
    ogDescription: post.value.seo_description ?? post.value.excerpt,
    ogImage: '/og-image.png',
    ogType: 'article',
    articlePublishedTime: post.value.published_at,
    twitterCard: 'summary_large_image',
    twitterImage: '/og-image.png',
  })

  // JSON-LD Article schema — gets us into Google's article-rich-results
  // surface and clarifies authorship for the social card unfurl.
  useStructuredData({
    '@type': 'Article',
    headline: post.value.title,
    description: post.value.seo_description ?? post.value.excerpt,
    image: ['/og-image.png'],
    datePublished: post.value.published_at,
    dateModified: post.value.published_at,
    author: { '@type': 'Organization', name: post.value.author },
    publisher: { '@type': 'Organization', name: 'Caseflow', logo: { '@type': 'ImageObject', url: '/og-image.png' } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `/blog/${post.value.slug}` },
  })
}

const verticalLabel = computed(() => post.value ? VERTICAL_LABELS[post.value.vertical] : '')
const verticalUrl   = computed(() => post.value ? `/${post.value.vertical}` : '/')

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })
</script>

<template>
  <article v-if="post" class="bg-white">
    <!-- ── Hero ─────────────────────────────────────────────── -->
    <header :class="['relative overflow-hidden bg-gradient-to-br', post.accent_from, post.accent_to]">
      <div class="absolute inset-0 bg-white/85"></div>
      <div class="relative mx-auto max-w-3xl px-6 pt-12 pb-10">
        <nav class="text-xs text-gray-600 mb-6 flex items-center gap-2">
          <NuxtLink to="/blog" class="hover:text-gray-900">Blog</NuxtLink>
          <span>/</span>
          <NuxtLink :to="verticalUrl" class="hover:text-gray-900">{{ verticalLabel }}</NuxtLink>
        </nav>
        <h1 class="text-3xl md:text-4xl font-bold tracking-tight text-gray-900">{{ post.title }}</h1>
        <p class="mt-3 text-base text-gray-600">{{ post.excerpt }}</p>
        <div class="mt-5 flex items-center gap-3 text-xs text-gray-500">
          <span>{{ post.author }}</span>
          <span>·</span>
          <time :datetime="post.published_at">{{ fmtDate(post.published_at) }}</time>
          <span>·</span>
          <span>{{ post.read_minutes }} min read</span>
        </div>
      </div>
    </header>

    <!-- ── Body ─────────────────────────────────────────────── -->
    <div class="mx-auto max-w-3xl px-6 py-12">
      <div class="prose-content text-gray-800 leading-relaxed">
        <slot />
      </div>

      <!-- Soft in-line CTA -->
      <aside :class="['mt-12 rounded-xl border p-6 bg-gradient-to-br', post.accent_from, post.accent_to, 'text-white']">
        <p class="text-xs uppercase tracking-wider opacity-80">For {{ verticalLabel.toLowerCase() }}</p>
        <p class="mt-1 text-lg font-semibold">Try Caseflow free for 14 days.</p>
        <p class="mt-1 text-sm opacity-90">We'll import your existing client list in under an hour. No credit card.</p>
        <div class="mt-4 flex gap-3 flex-wrap">
          <NuxtLink to="/signup" class="rounded-lg bg-white text-gray-900 px-4 py-2 text-sm font-medium hover:bg-gray-100">Start free trial</NuxtLink>
          <NuxtLink :to="verticalUrl" class="rounded-lg border border-white/40 text-white px-4 py-2 text-sm font-medium hover:bg-white/10">See the {{ verticalLabel.toLowerCase() }} page</NuxtLink>
        </div>
      </aside>
    </div>

    <!-- ── Related posts ────────────────────────────────────── -->
    <section v-if="related.length" class="bg-gray-50 py-16">
      <div class="mx-auto max-w-5xl px-6">
        <h2 class="text-2xl font-semibold text-gray-900">Keep reading</h2>
        <div class="mt-6 grid md:grid-cols-3 gap-4">
          <NuxtLink
            v-for="r in related" :key="r.slug"
            :to="`/blog/${r.slug}`"
            class="block rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md transition"
          >
            <p :class="['text-[10px] uppercase tracking-wider font-medium bg-clip-text text-transparent bg-gradient-to-r', r.accent_from, r.accent_to]">
              {{ VERTICAL_LABELS[r.vertical] }}
            </p>
            <h3 class="mt-2 font-semibold text-gray-900 text-sm leading-snug">{{ r.title }}</h3>
            <p class="mt-2 text-xs text-gray-500">{{ r.read_minutes }} min read</p>
          </NuxtLink>
        </div>
      </div>
    </section>
  </article>

  <div v-else class="mx-auto max-w-3xl px-6 py-20 text-center text-gray-500">
    Post not found. <NuxtLink to="/blog" class="text-indigo-600 hover:underline">Back to the blog</NuxtLink>.
  </div>
</template>

<style scoped>
/* Light prose styling — Tailwind Typography is overkill for the content we have. */
.prose-content :deep(h2) { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.75rem; color: #111827; }
.prose-content :deep(h3) { font-size: 1.125rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #1f2937; }
.prose-content :deep(p)  { margin-bottom: 1rem; line-height: 1.75; }
.prose-content :deep(ul), .prose-content :deep(ol) { margin: 0 0 1rem 1.25rem; }
.prose-content :deep(li) { margin-bottom: 0.375rem; }
.prose-content :deep(a)  { color: #4f46e5; text-decoration: underline; }
.prose-content :deep(blockquote) { border-left: 3px solid #e5e7eb; padding-left: 1rem; color: #4b5563; font-style: italic; margin: 1.5rem 0; }
.prose-content :deep(code) { background: #f3f4f6; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875em; }
.prose-content :deep(strong) { color: #111827; }
.prose-content :deep(hr) { border: none; border-top: 1px solid #e5e7eb; margin: 2rem 0; }
</style>
